import { useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate } from '../../types/taOutreach';
import type { BulkSelection } from './useBulkSelection';

/**
 * Write & send › approve many at once (Sofia, Oct 28 2026: "alle auf einmal anklicken"). The cards
 * carry a checkbox; this bar selects all and approves the ticked ones in ONE request (Nov 2 2026:
 * "bulk approve is slow" — it was one `move: 'ready'` after another): `approveMany` gives every id
 * exactly the card's own Approve — a colleague's draft is put together for the viewer on the
 * server, a hand-edited one comes back `resigned` — and answers per id. "Start" in the Send box
 * then sends them.
 */

export function BulkApprove({ selection, onUpdated }: { selection: BulkSelection; onUpdated: (item: OutreachCandidate) => void }) {
  const { approvable: rows, selected, allOn, selectAll, stillDrafting } = selection;
  const [progress, setProgress] = useState<number | null>(null);
  if (!rows.length && progress === null) return null;
  const picked = rows.filter((c) => selected.has(c.id));
  const all = allOn || (picked.length === rows.length && rows.length > 0);

  async function approveAll() {
    const todo = picked;
    let approved = 0;
    const held: string[] = [];
    const failed: string[] = [];
    const nameOf = (id: string) => todo.find((c) => c.id === id)?.name || id;
    setProgress(todo.length);
    try {
      const { results } = await api.approveMany(todo.map((c) => c.id));
      for (const r of results) {
        if (!r.item) {
          failed.push(`${nameOf(r.id)}: ${r.error || 'could not approve'}`);
          continue;
        }
        onUpdated(r.item);
        // A hand-edited draft put together as the viewer: its edits are gone, so it waits for one more look.
        if (r.resigned || r.item.status !== 'ready') held.push(nameOf(r.id));
        else approved += 1;
      }
    } catch (err) {
      failed.push(err instanceof Error ? err.message : 'could not approve');
    } finally {
      setProgress(null);
      selectAll(false);
    }
    const parts = [`${approved} approved`];
    if (held.length) parts.push(`${held.length} to check again (edits replaced): ${held.join(', ')}`);
    if (failed.length) parts.push(`not approved — ${failed.join('; ')}`);
    showToast({ status: held.length || failed.length ? 'error' : 'success', position: 'bottomCenter', description: parts.join(' · ') });
  }

  return (
    <span className="flex items-center gap-2.5">
      <label className="flex items-center gap-1.5 text-xs">
        <input type="checkbox" checked={all} disabled={progress !== null} onChange={(e) => selectAll(e.target.checked)} />
        Select all
      </label>
      <button
        type="button"
        className="h-8 rounded-md border border-solid border-[#1A1A1A] bg-[#1A1A1A] px-3.5 text-xs font-semibold text-white hover:bg-white hover:text-[#1A1A1A] disabled:opacity-40"
        disabled={progress !== null || !picked.length}
        onClick={() => void approveAll()}
      >
        {progress !== null
          ? `Approving ${progress}…`
          : [`Approve ${picked.length || ''}`.trim(), stillDrafting ? `${stillDrafting} still drafting` : null].filter(Boolean).join(' · ')}
      </button>
    </span>
  );
}

/** A card's tick; its own click, so ticking a card never opens it. */
export function SelectBox({ name, selected, onSelect }: { name: string; selected?: boolean; onSelect: (on: boolean) => void }) {
  return (
    <input
      type="checkbox"
      aria-label={`Select ${name}`}
      checked={Boolean(selected)}
      onChange={(e) => onSelect(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className="mt-1 shrink-0"
    />
  );
}
